import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Trash2, UserPlus, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';
import { useRemoveMember, useUpsertMember, useWorkspaceMembers, type Membership } from '@/lib/api';
import { InviteMemberDialog } from '@/components/workspaces/InviteMemberDialog';

const ROLE_OPTIONS = ['owner', 'admin', 'translator', 'viewer'] as const;

type Role = (typeof ROLE_OPTIONS)[number];

const ROLE_TONE: Record<Role, 'primary' | 'positive' | 'muted' | 'warning'> = {
  owner: 'positive',
  admin: 'primary',
  translator: 'muted',
  viewer: 'warning',
};

export function MembersTab() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const members = useWorkspaceMembers(wsId);
  const toast = useToast();
  const upsert = useUpsertMember();
  const remove = useRemoveMember();
  const [inviting, setInviting] = useState(false);

  async function changeRole(m: Membership, role: Role) {
    try {
      await upsert.mutateAsync({
        workspaceID: wsId,
        userSubject: m.userSubject,
        email: m.email,
        displayName: m.displayName,
        role,
      });
      toast.show(`Updated role for ${memberLabel(m)}`, 'success');
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  async function removeMember(m: Membership) {
    if (!window.confirm(`Revoke ${memberLabel(m)}'s access to this workspace?`)) return;
    try {
      await remove.mutateAsync({ workspaceID: wsId, userSubject: m.userSubject });
      toast.show(`Removed ${memberLabel(m)}`, 'success');
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button onClick={() => setInviting(true)}>
          <UserPlus aria-hidden />
          Invite member
        </Button>
      </div>

      {members.isLoading && <Spinner label="Loading members…" />}

      {members.isError && (
        <p
          role="alert"
          className="rounded-md border border-negative/40 bg-negative/10 p-3 text-sm text-negative"
        >
          Failed to load members: {members.error.message}
        </p>
      )}

      {members.isSuccess && members.data.length === 0 && (
        <EmptyState
          icon={Users}
          title="No members yet"
          description="Invite teammates with their Keycloak subject id and pick a workspace role."
          action={<Button onClick={() => setInviting(true)}>Invite the first member</Button>}
        />
      )}

      {members.isSuccess && members.data.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Member</th>
                <th className="px-4 py-2 text-left font-medium">Subject</th>
                <th className="px-4 py-2 text-left font-medium">Role</th>
                <th className="px-4 py-2 text-right font-medium">Joined</th>
                <th className="w-12 px-2" />
              </tr>
            </thead>
            <tbody>
              {members.data.map((m) => (
                <tr key={m.id} className="border-t border-border">
                  <td className="px-4 py-2">
                    <div className="font-medium text-foreground">{memberLabel(m)}</div>
                    {m.email && (
                      <div className="text-xs text-muted-foreground">{m.email}</div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                      {m.userSubject}
                    </code>
                  </td>
                  <td className="px-4 py-2">
                    <label className="sr-only" htmlFor={`role-${m.id}`}>
                      Role for {memberLabel(m)}
                    </label>
                    <div className="inline-flex items-center gap-2">
                      <select
                        id={`role-${m.id}`}
                        value={m.role}
                        onChange={(e) => void changeRole(m, e.target.value as Role)}
                        className="h-8 rounded-md border border-input bg-surface px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <Badge tone={ROLE_TONE[m.role]}>{m.role}</Badge>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right text-xs text-muted-foreground">
                    {new Date(m.joinedAt).toLocaleDateString()}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Remove ${memberLabel(m)}`}
                      onClick={() => void removeMember(m)}
                      disabled={remove.isPending}
                    >
                      <Trash2 aria-hidden className="text-negative" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InviteMemberDialog
        open={inviting}
        onClose={() => setInviting(false)}
        pending={upsert.isPending}
        onSubmit={async (input) => {
          try {
            await upsert.mutateAsync({ workspaceID: wsId, ...input });
            toast.show('Invitation sent', 'success');
            setInviting(false);
          } catch (err) {
            toast.show((err as Error).message, 'error');
          }
        }}
      />
    </div>
  );
}

function memberLabel(m: Membership): string {
  return m.displayName || m.email || m.userSubject;
}
